/**
 * Public Upload Portal — single bulk form.
 *
 * Flow:
 *   1. Password screen
 *   2. Single form with global fields (name + upload date + notes) and
 *      4 optional screenshot slots (one per report type). User can attach
 *      any subset and click the one Submit button to upload them all at once.
 *   3. Success screen with a per-report summary + "Upload another" reset.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Lock, Upload, CheckCircle2, X, Image as ImageIcon, Calendar, User, AlertTriangle, LogOut, RefreshCw, FileText, TrendingUp, Activity, BarChart3, Sparkles, Send,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const SESSION_PREFIX = 'salesUploadsPortal:';
const REPORT_ICON = {
  lead_source: TrendingUp,
  daily_hot_conversion: Activity,
  utr_ownerwise: BarChart3,
  hot_lead_distribution: FileText,
};
const REPORT_COLOR = {
  lead_source: 'violet',
  daily_hot_conversion: 'rose',
  utr_ownerwise: 'emerald',
  hot_lead_distribution: 'amber',
};
const COLOR_BG = { violet: 'bg-violet-50 border-violet-200', rose: 'bg-rose-50 border-rose-200', emerald: 'bg-emerald-50 border-emerald-200', amber: 'bg-amber-50 border-amber-200' };
const COLOR_TXT = { violet: 'text-violet-700', rose: 'text-rose-700', emerald: 'text-emerald-700', amber: 'text-amber-700' };

const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api/public/sales-uploads`;

export default function PublicUploadPortal() {
  const { linkId: slug } = useParams();
  const sessionKey = `${SESSION_PREFIX}${slug}`;

  const [password, setPassword] = useState(() => {
    try { return sessionStorage.getItem(sessionKey) || ''; } catch { return ''; }
  });
  const [linkInfo, setLinkInfo] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');
  const [recent, setRecent] = useState([]);
  const [pendingPwd, setPendingPwd] = useState('');

  // ── Shared form state ──
  const [uploaderName, setUploaderName] = useState(() => {
    try { return localStorage.getItem(`${sessionKey}:name`) || ''; } catch { return ''; }
  });
  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const [uploadDate, setUploadDate] = useState(today);
  const [notes, setNotes] = useState('');

  // Per-report file slots:  { [reportType]: { file, preview } }
  const [slots, setSlots] = useState({});

  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // array of { report_type, ok, rows, error }

  // ── Verify link + pull recent ──
  const verify = useCallback(async (pwd) => {
    setVerifying(true);
    setVerifyError('');
    try {
      const { data } = await axios.post(`${apiBase}/verify`, { slug, password: pwd });
      setLinkInfo(data);
      setPassword(pwd);
      try { sessionStorage.setItem(sessionKey, pwd); } catch {}
      try {
        const { data: rec } = await axios.post(`${apiBase}/recent`, { slug, password: pwd });
        setRecent(rec || []);
      } catch {}
    } catch (e) {
      const status = e.response?.status;
      setLinkInfo(null);
      if (status === 401) setVerifyError('Invalid password');
      else if (status === 404) setVerifyError('Link not found or revoked');
      else if (status === 410) setVerifyError('This link has expired');
      else if (status === 429) setVerifyError('Upload limit reached for this link');
      else setVerifyError(e.response?.data?.detail || 'Failed to verify');
      try { sessionStorage.removeItem(sessionKey); } catch {}
    } finally { setVerifying(false); }
  }, [slug, sessionKey]);

  useEffect(() => {
    if (password) verify(password);
    else setVerifying(false);
  }, [password, verify]);

  const onLogin = (e) => {
    e?.preventDefault();
    if (!pendingPwd.trim()) return;
    verify(pendingPwd.trim());
  };

  const logout = () => {
    try { sessionStorage.removeItem(sessionKey); } catch {}
    setPassword(''); setLinkInfo(null); setPendingPwd('');
  };

  useEffect(() => {
    if (uploaderName) { try { localStorage.setItem(`${sessionKey}:name`, uploaderName); } catch {} }
  }, [uploaderName, sessionKey]);

  // ── Per-slot file handler ──
  const onPickFile = (reportType, f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Only image files allowed');
    if (f.size > 8 * 1024 * 1024) return toast.error('Max 8 MB');
    const reader = new FileReader();
    reader.onload = (e) => setSlots(prev => ({ ...prev, [reportType]: { file: f, preview: e.target.result } }));
    reader.readAsDataURL(f);
  };
  const clearSlot = (reportType) => setSlots(prev => { const n = { ...prev }; delete n[reportType]; return n; });

  const resetForm = () => {
    setSlots({}); setNotes(''); setResults(null);
    setUploadDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const attachedCount = Object.values(slots).filter(s => s?.file).length;

  // ── Submit: upload each attached slot in parallel ──
  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!uploaderName.trim()) return toast.error('Please enter your name');
    if (attachedCount === 0) return toast.error('Attach at least one screenshot');
    if (!uploadDate) return toast.error('Please pick the upload date');
    setBusy(true);

    const reportTypes = (linkInfo.allowed_report_types || []);
    const tasks = reportTypes
      .filter(rt => slots[rt]?.file)
      .map(async (rt) => {
        try {
          const fd = new FormData();
          fd.append('slug', slug);
          fd.append('password', password);
          fd.append('report_type', rt);
          fd.append('uploader_name', uploaderName.trim());
          fd.append('upload_date', uploadDate);
          fd.append('notes', notes.trim());
          fd.append('file', slots[rt].file);
          const { data } = await axios.post(`${apiBase}/upload`, fd);
          return { report_type: rt, ok: data.status === 'extracted', rows: data.extracted_rows || [], error: data.extraction_error, superseded: data.superseded_count || 0 };
        } catch (err) {
          return { report_type: rt, ok: false, rows: [], error: err.response?.data?.detail || 'Upload failed' };
        }
      });

    const out = await Promise.all(tasks);
    setResults(out);
    verify(password); // refresh used_count + recent
    const okCount = out.filter(o => o.ok).length;
    if (okCount === out.length) toast.success(`All ${out.length} screenshot${out.length === 1 ? '' : 's'} uploaded & extracted`);
    else if (okCount > 0) toast.warning(`${okCount}/${out.length} extracted, ${out.length - okCount} failed`);
    else toast.error('All uploads failed — check errors below');
    setBusy(false);
  };

  // ── Renderers ──
  if (verifying) return <CenteredCard tone="violet" icon={RefreshCw} title="Verifying link…" body="One moment." spin />;

  if (!linkInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-rose-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm shadow-xl border-violet-100">
          <CardContent className="p-6 space-y-4">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-rose-500 mx-auto flex items-center justify-center shadow-md"><Upload className="w-6 h-6 text-white" /></div>
              <h1 className="text-lg font-bold">Sales Reports Upload</h1>
              <p className="text-xs text-slate-500">Enter the password your admin shared with you.</p>
            </div>
            {verifyError && (
              <div className="rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" />{verifyError}
              </div>
            )}
            <form onSubmit={onLogin} className="space-y-3">
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" value={pendingPwd} onChange={e => setPendingPwd(e.target.value)} placeholder="Enter link password" className="h-9" autoFocus data-testid="portal-password" />
              </div>
              <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="portal-login-btn"><Lock className="w-3.5 h-3.5 mr-2" />Sign in</Button>
            </form>
            <p className="text-[10px] text-center text-slate-400">Slug <span className="font-mono">{slug}</span></p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center"><Upload className="w-4 h-4 text-white" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">{linkInfo.label || 'Sales reports'}</p>
          <p className="text-[10px] text-slate-500">{linkInfo.used_count ?? 0} upload{(linkInfo.used_count ?? 0) === 1 ? '' : 's'} so far</p>
        </div>
        {uploaderName && <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{uploaderName}</Badge>}
        <Button size="sm" variant="ghost" onClick={logout} className="text-xs text-slate-500"><LogOut className="w-3.5 h-3.5 mr-1" />Sign out</Button>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {results ? (
          <SuccessCard
            results={results}
            linkInfo={linkInfo}
            onAnother={resetForm}
            onLogout={logout}
          />
        ) : (
          <form onSubmit={onSubmit} className="space-y-3" data-testid="upload-form">
            <Card className="border shadow-sm">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  <h2 className="text-sm font-bold text-slate-800">Upload daily sales report screenshots</h2>
                  <span className="ml-auto text-[10px] text-slate-500">Auto-extracted via Gemini Vision</span>
                </div>

                {/* Your name */}
                <div>
                  <Label className="text-xs">Your name <span className="text-rose-500">*</span></Label>
                  <div className="relative">
                    <User className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input value={uploaderName} onChange={e => setUploaderName(e.target.value)} placeholder="e.g. Ritu Sharma" className="h-9 text-sm pl-8" data-testid="portal-uploader-name" required />
                  </div>
                </div>

                {/* Upload date */}
                <div>
                  <Label className="text-xs">Upload date <span className="text-rose-500">*</span></Label>
                  <div className="relative">
                    <Calendar className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input type="date" value={uploadDate} onChange={e => setUploadDate(e.target.value)} className="h-9 text-sm pl-8" max={today} data-testid="portal-upload-date" required />
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Defaults to today — change if you're uploading screenshots from an earlier day.</p>
                </div>

                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-700 mb-1">Attach screenshots</p>
                  <p className="text-[10px] text-slate-500 mb-3">Add a screenshot for any of the 4 reports below. You can attach all four in one go, or just one — only attached cards will be submitted.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(linkInfo.allowed_report_types || []).map(rt => (
                      <ReportSlot
                        key={rt}
                        rt={rt}
                        meta={linkInfo.report_schema?.[rt]}
                        slot={slots[rt]}
                        onPick={(f) => onPickFile(rt, f)}
                        onClear={() => clearSlot(rt)}
                      />
                    ))}
                  </div>
                </div>

                {/* Global notes */}
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything the admin should know about today's screenshots" className="text-sm resize-none" data-testid="portal-notes" />
                </div>

                {/* Submit */}
                <Button type="submit" disabled={busy || attachedCount === 0 || !uploaderName.trim()} className="w-full h-11 bg-violet-600 hover:bg-violet-700 text-sm" data-testid="portal-submit">
                  {busy
                    ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Uploading {attachedCount} screenshot{attachedCount === 1 ? '' : 's'} & extracting…</>
                    : <><Send className="w-4 h-4 mr-2" />Submit {attachedCount > 0 ? `${attachedCount} screenshot${attachedCount === 1 ? '' : 's'}` : ''}</>}
                </Button>
              </CardContent>
            </Card>
          </form>
        )}

        {!results && null}

      </main>
    </div>
  );
}

// ── Per-report screenshot slot ─────────────────────────────
function ReportSlot({ rt, meta, slot, onPick, onClear }) {
  const Icon = REPORT_ICON[rt] || FileText;
  const color = REPORT_COLOR[rt] || 'violet';
  const attached = !!slot?.file;
  return (
    <div className={`rounded border-2 transition-all ${attached ? COLOR_BG[color] + ' shadow-sm' : 'border-slate-200 bg-white'}`} data-testid={`portal-slot-${rt}`}>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${COLOR_TXT[color]} flex-shrink-0`} />
          <p className="text-xs font-bold text-slate-800 leading-tight flex-1 truncate">{meta?.label || rt}</p>
          {attached && <Badge className={`bg-${color}-500 text-white text-[9px] flex-shrink-0`}><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Attached</Badge>}
        </div>
        {attached ? (
          <div className="relative rounded border border-slate-200 overflow-hidden bg-white">
            <img src={slot.preview} alt="" className="w-full h-28 object-contain" />
            <button type="button" onClick={onClear} className="absolute top-1 right-1 bg-white/95 hover:bg-white rounded-full p-1 shadow" data-testid={`portal-clear-${rt}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-28 rounded border-2 border-dashed border-slate-300 hover:border-violet-400 cursor-pointer bg-white transition-colors">
            <ImageIcon className="w-5 h-5 text-slate-300 mb-1" />
            <p className="text-[11px] text-slate-500 font-medium">Attach screenshot</p>
            <p className="text-[9px] text-slate-400">PNG / JPG / WEBP</p>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => onPick(e.target.files?.[0])} data-testid={`portal-file-${rt}`} />
          </label>
        )}
      </div>
    </div>
  );
}

// ── Success confirmation ───────────────────────────────────
function SuccessCard({ results, linkInfo, onAnother, onLogout }) {
  const okCount = results.filter(r => r.ok).length;
  const totalRows = results.reduce((s, r) => s + r.rows.length, 0);
  const allOk = okCount === results.length;
  return (
    <Card className={`border-2 ${allOk ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`} data-testid="upload-success-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${allOk ? 'bg-emerald-500' : 'bg-amber-500'}`}>
            {allOk ? <CheckCircle2 className="w-5 h-5 text-white" /> : <AlertTriangle className="w-5 h-5 text-white" />}
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-slate-800">{allOk ? `All ${results.length} screenshot${results.length === 1 ? '' : 's'} uploaded` : `${okCount} of ${results.length} uploaded`}</h2>
            <p className="text-xs text-slate-600 mt-0.5">{totalRows} row{totalRows === 1 ? '' : 's'} auto-extracted · admin can see them instantly in the dashboard.</p>
          </div>
        </div>

        <div className="space-y-2">
          {results.map(r => {
            const Icon = REPORT_ICON[r.report_type] || FileText;
            const color = REPORT_COLOR[r.report_type] || 'violet';
            return (
              <div key={r.report_type} className={`rounded border p-3 ${r.ok ? 'bg-white border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${COLOR_TXT[color]} flex-shrink-0`} />
                  <p className="text-xs font-bold text-slate-800 flex-1">{linkInfo.report_schema?.[r.report_type]?.label || r.report_type}</p>
                  {r.ok
                    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><CheckCircle2 className="w-2.5 h-2.5 mr-1" />{r.rows.length} rows</Badge>
                    : <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]"><AlertTriangle className="w-2.5 h-2.5 mr-1" />Failed</Badge>}
                </div>
                {!r.ok && r.error && <p className="text-[10px] text-rose-700 mt-1 pl-6">{r.error}</p>}
                {r.ok && r.rows.length > 0 && (
                  <div className="pl-6 text-[10px] text-slate-500 mt-1 truncate">
                    {r.rows.slice(0, 3).map((row, i) => (
                      <span key={i} className="inline-block mr-2">{Object.entries(row).filter(([k]) => k !== 'id').map(([, v]) => v).join(' · ')}</span>
                    ))}
                    {r.rows.length > 3 && <span className="text-slate-400">+{r.rows.length - 3} more</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-200/60">
          <Button onClick={onAnother} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="upload-another-btn"><Upload className="w-3.5 h-3.5 mr-2" />Upload more</Button>
          <Button variant="outline" onClick={onLogout} className="text-xs" data-testid="upload-done-btn"><LogOut className="w-3.5 h-3.5 mr-1" />Done</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CenteredCard({ tone, icon: Icon, title, body, spin }) {
  const tint = tone === 'rose' ? 'bg-rose-50 border-rose-200 text-rose-800' : tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-violet-50 border-violet-200 text-violet-800';
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className={`w-full max-w-md ${tint}`}>
        <CardContent className="p-8 text-center space-y-3">
          <Icon className={`w-12 h-12 mx-auto ${spin ? 'animate-spin' : ''} ${tone === 'rose' ? 'text-rose-500' : tone === 'amber' ? 'text-amber-500' : 'text-violet-500'}`} />
          <h2 className="text-lg font-bold">{title}</h2>
          <p className="text-sm">{body}</p>
        </CardContent>
      </Card>
    </div>
  );
}
